import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { createPortal } from "react-dom";
import { useScopedDisabled } from "../hooks/useClientReadOnly";

export function CustomSelector({
  value,
  onChange,
  options = [],
  placeholder = "Seleccionar...",
  disabled: disabledProp,
  size = "default", // "sm" | "default"
  searchable = false, // Nueva prop para habilitar búsqueda
  freeSolo = false, // Nueva prop para permitir valores personalizados no en la lista
  placeholderValues = [], // Valores que deben mostrarse con estilo de placeholder
  leadingIcon = null,
  renderMenuContent = null,
  menuWidth = null,
  menuMaxHeight = null,
  menuEstimatedHeight = null,
  menuPadding = 0,
  triggerVariant = "boxed", // "boxed" | "minimal": bare text + chevron, same dropdown
}) {
  const disabled = useScopedDisabled(disabledProp);
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  // Borde de foco neutro (negro) en vez del color de marca.
  const focusBorder = neutrals?.black ?? "#000000";
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [focused, setFocused] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find((opt) => opt.value === value && !opt.disabled) || null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const shouldRenderCustomMenu = typeof renderMenuContent === "function";

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideTrigger = triggerRef.current?.contains(event.target);
      const clickedInsideMenu = dropdownMenuRef.current?.contains(event.target);

      if (!clickedInsideTrigger && !clickedInsideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recalculate dropdown position when it opens or on window resize/scroll
  useEffect(() => {
    function updatePosition() {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const maxDropdownHeight = shouldRenderCustomMenu
          ? menuMaxHeight || 360
          : 200;
        const estimatedDropdownHeight = shouldRenderCustomMenu
          ? menuEstimatedHeight || Math.min(maxDropdownHeight, 320)
          : 150;
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const resolvedWidth =
          typeof menuWidth === "number" && menuWidth > 0 ? menuWidth : rect.width;
        const horizontalMargin = 16;
        const availableWidth = Math.max(
          rect.width,
          viewportWidth - horizontalMargin * 2
        );
        const width = Math.min(resolvedWidth, availableWidth);

        // Only open upwards if there's really not enough space below
        // and there's more space above (prefer downwards opening)
        const shouldOpenUpwards =
          spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

        let top, maxHeight;

        if (shouldOpenUpwards) {
          // Open upwards - position at the top of trigger, will use transform to move it up
          const availableHeight = Math.min(maxDropdownHeight, spaceAbove - 20);
          maxHeight = Math.max(100, availableHeight);
          // Position at the top edge of the trigger, transform will move it up by its own height
          top = rect.top + window.scrollY - 4;
        } else {
          // Open downwards (default)
          const availableHeight = Math.min(maxDropdownHeight, spaceBelow - 20);
          maxHeight = Math.max(100, availableHeight);
          top = rect.bottom + window.scrollY + 4;
        }

        const minLeft = window.scrollX + horizontalMargin;
        const maxLeft = window.scrollX + viewportWidth - horizontalMargin - width;
        const left = Math.min(
          Math.max(rect.left + window.scrollX, minLeft),
          Math.max(minLeft, maxLeft)
        );

        setDropdownPos({
          top,
          left,
          width,
          maxHeight: menuMaxHeight || maxHeight,
          openUpwards: shouldOpenUpwards
        });
      }
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [
    isOpen,
    menuEstimatedHeight,
    menuMaxHeight,
    menuWidth,
    shouldRenderCustomMenu,
  ]);

  useEffect(() => {
    const option = options.find((opt) => opt.value === value && !opt.disabled);
    if (option) {
      setSelectedOption(option);
    } else if (freeSolo && value) {
      // Si freeSolo está activado y hay un valor que no coincide con ninguna opción,
      // crear una opción sintética para mostrar el valor personalizado
      setSelectedOption({ value: value, label: value });
    } else {
      setSelectedOption(null);
    }
  }, [value, options, freeSolo]);

  // Filtrar opciones cuando cambia el término de búsqueda
  useEffect(() => {
    if (searchable && searchTerm) {
      const filtered = options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options, searchable]);

  // Limpiar búsqueda al cerrar el dropdown (solo si no searchable)
  useEffect(() => {
    if (!isOpen && searchable) {
      setSearchTerm("");
    }
  }, [isOpen, searchable]);

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange(option.value);
    setIsOpen(false);
    setFocused(false);
    setSearchTerm(""); // Limpiar búsqueda al seleccionar
  };

  // Manejar freeSolo cuando se pierde el foco
  const handleFreeSoloBlur = () => {
    if (freeSolo && searchTerm.trim()) {
      const matchedOption = options.find(
        (opt) => opt.label.toLowerCase() === searchTerm.toLowerCase()
      );
      if (!matchedOption) {
        // Usar el término de búsqueda como valor personalizado
        const customValue = searchTerm.trim();
        setSelectedOption({ value: customValue, label: customValue });
        onChange(customValue);
      }
    }
  };

  const sizeStyles = {
    sm: {
      height: "32px",
      fontSize: "0.75rem",
      padding: "6px 8px",
    },
    default: {
      height: "36px",
      fontSize: "0.875rem",
      padding: "8px 12px",
    },
  };

  const currentSize = sizeStyles[size];
  const isMinimal = triggerVariant === "minimal";

  const baseStyles = {
    width: isMinimal ? "auto" : "100%",
    height: isMinimal ? "auto" : currentSize.height,
    padding: isMinimal ? "2px 0" : currentSize.padding,
    paddingRight: isMinimal ? 0 : "12px",
    fontSize: currentSize.fontSize,
    fontFamily: theme.typography.fontFamily,
    fontWeight: isMinimal ? 600 : "inherit",
    backgroundColor: isMinimal
      ? "transparent"
      : disabled
        ? theme.palette.action.disabledBackground
        : theme.palette.background.default,
    border: isMinimal
      ? "none"
      : `1px solid ${theme.palette.border?.input || theme.palette.divider}`,
    borderRadius: isMinimal ? 0 : "8px",
    color: disabled
      ? theme.palette.text.disabled
      : (isOpen && searchTerm)
        ? theme.palette.text.primary
        : selectedOption && placeholderValues.includes(selectedOption.value)
          ? theme.palette.text.muted || theme.palette.text.disabled
          : selectedOption
            ? theme.palette.text.primary
            : theme.palette.text.muted || theme.palette.text.disabled,
    cursor: disabled ? "not-allowed" : searchable ? "text" : "pointer",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.2s ease",
    outline: "none",
    ...(disabled && {
      opacity: 0.6,
    }),
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* Trigger - Input si es searchable, Button si no */}
      {searchable ? (
        <div
          ref={triggerRef}
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          <input
            ref={searchInputRef}
            type="text"
            value={isOpen ? searchTerm : (selectedOption ? selectedOption.label : "")}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (!isOpen) {
                setIsOpen(true);
              }
            }}
            onFocus={() => {
              if (!disabled) {
                setFocused(true);
                setIsOpen(true);
              }
            }}
            onBlur={() => {
              if (!disabled) {
                setFocused(false);
                handleFreeSoloBlur();
              }
            }}
            onClick={() => {
              if (!disabled && !isOpen) {
                setIsOpen(true);
              }
            }}
            disabled={disabled}
            placeholder={placeholder}
            style={{
              ...baseStyles,
              paddingRight: "36px",
              ...(leadingIcon && { paddingLeft: "36px" }),
              ...(focused || isOpen
                ? {
                  borderColor: focusBorder,
                  boxShadow: "none",
                }
                : {}),
            }}
            onMouseEnter={(e) => {
              if (!disabled && !focused && !isOpen) {
                e.target.style.borderColor =
                  theme.palette.border?.default || theme.palette.divider;
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled && !focused && !isOpen) {
                e.target.style.borderColor =
                  theme.palette.border?.input || theme.palette.divider;
              }
            }}
          />
          {leadingIcon && (
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                pointerEvents: "none",
              }}
            >
              {leadingIcon}
            </span>
          )}
          <div
            onClick={() => !disabled && setIsOpen(!isOpen)}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              pointerEvents: disabled ? "none" : "auto",
            }}
          >
            <ChevronDown
              size={16}
              style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                color: theme.palette.text.secondary,
              }}
            />
          </div>
        </div>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onFocus={() => {
            if (!disabled) {
              setFocused(true);
            }
          }}
          onBlur={() => {
            if (!disabled) {
              setFocused(false);
            }
          }}
          disabled={disabled}
          onMouseEnter={(e) => {
            if (isMinimal) return;
            if (!disabled && !focused && !isOpen) {
              e.target.style.borderColor =
                theme.palette.border?.default || theme.palette.divider;
            }
          }}
          onMouseLeave={(e) => {
            if (isMinimal) return;
            if (!disabled && !focused && !isOpen) {
              e.target.style.borderColor =
                theme.palette.border?.input || theme.palette.divider;
            }
          }}
          style={{
            ...baseStyles,
            ...(!isMinimal && (focused || isOpen)
              ? {
                borderColor: focusBorder,
                boxShadow: "none",
              }
              : {}),
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMinimal ? 4 : 8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {leadingIcon && (
              <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                {leadingIcon}
              </span>
            )}
            {selectedOption?.icon && (
              <span style={{ display: "flex", alignItems: "center" }}>
                {selectedOption.icon}
              </span>
            )}
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </span>
          <ChevronDown
            size={isMinimal ? 14 : 16}
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              flexShrink: 0,
              marginLeft: "0px",
              color: isMinimal ? theme.palette.text.secondary : undefined,
            }}
          />
        </button>
      )}

      {/* Dropdown Menu rendered in portal to ensure it sits above all content */}
      {isOpen &&
        !disabled &&
        dropdownPos &&
        (shouldRenderCustomMenu || !(freeSolo && filteredOptions.length === 0)) &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            style={{
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              boxShadow: theme.shadows[3],
              zIndex: 2147483647, // Max 32-bit int to outrank other overlays
              maxHeight: `${dropdownPos.maxHeight}px`,
              overflowY: "auto",
              overflowX: "hidden",
              transform: dropdownPos.openUpwards ? "translateY(-100%)" : "none",
              padding: shouldRenderCustomMenu ? menuPadding : 0,
            }}
          >
            {shouldRenderCustomMenu ? (
              renderMenuContent({
                closeDropdown: () => {
                  setIsOpen(false);
                  setFocused(false);
                },
                dropdownMaxHeight: dropdownPos.maxHeight,
                dropdownWidth: dropdownPos.width,
                openUpwards: dropdownPos.openUpwards,
              })
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => !option.disabled && handleSelect(option)}
                  disabled={option.disabled}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: "transparent",
                    border: "none",
                    color: option.disabled
                      ? theme.palette.text.secondary
                      : theme.palette.text.primary,
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background-color 0.2s ease",
                    outline: "none",
                    fontStyle: option.disabled ? "italic" : "normal",
                    ...(selectedOption?.value === option.value && !option.disabled && {
                      backgroundColor: theme.palette.grey[100],
                      fontWeight: 500,
                    }),
                  }}
                  onMouseEnter={(e) => {
                    if (!option.disabled) {
                      e.target.style.backgroundColor = theme.palette.action.hover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!option.disabled) {
                      if (selectedOption?.value !== option.value) {
                        e.target.style.backgroundColor = "transparent";
                      } else {
                        e.target.style.backgroundColor = theme.palette.grey[100];
                      }
                    }
                  }}
                >
                  {option.icon && (
                    <span style={{ display: "flex", alignItems: "center" }}>
                      {option.icon}
                    </span>
                  )}
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {option.label}
                  </span>
                </button>
              ))
            ) : (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  fontSize: "0.875rem",
                }}
              >
                No se encontraron resultados
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
